import React, { memo } from 'react';
import { FixedSizeList } from 'react-window';

/**
 * Virtual scrolling table component for calls in CampaignDetails
 * Uses div-based grid layout styled as table for better performance
 */
const VirtualCallTable = ({
  calls = [],
  contacts = [],
  getStatusBadge,
  getCallCategoryValue,
  getLeadQualityValue,
  renderCategoryBadge,
  renderLeadQualityBadge,
  renderEnrichmentFields,
  formatDuration,
  formatDate,
  downloadCallRecording,
  downloadCallTranscript,
  height = 600,
  hasMore = false,
  loading = false,
  observerTarget = null
}) => {
  const TableRow = memo(({ index, style, data }) => {
    const call = data.calls[index];
    if (!call) return null;

    const contact = data.contacts.find(
      c => (c.phone || c.telephone) === (call.phone || call.receiver)
    );

    return (
      <div style={style} className="border-b border-gray-200 hover:bg-gray-50">
        <div className="grid grid-cols-[minmax(100px,1fr)_minmax(100px,1fr)_minmax(80px,0.8fr)_minmax(100px,1fr)_minmax(100px,1fr)_minmax(200px,2fr)_minmax(60px,0.6fr)_minmax(120px,1.2fr)_minmax(150px,1.5fr)] gap-3 px-6 py-4 items-start">
          <div className="text-sm text-gray-900 whitespace-nowrap min-w-0 overflow-hidden">
            {contact ? `${contact.nom || contact.name || ''} ${contact.prenom || contact.firstName || ''}`.trim() : '-'}
          </div>
          <div className="text-sm text-gray-900 whitespace-nowrap min-w-0 overflow-hidden">
            {call.phone}
          </div>
          <div className="whitespace-nowrap min-w-0">
            {data.getStatusBadge(call.status)}
          </div>
          <div className="text-sm text-gray-900 whitespace-nowrap min-w-0 overflow-hidden">
            {data.renderCategoryBadge(data.getCallCategoryValue(call))}
          </div>
          <div className="text-sm text-gray-900 whitespace-nowrap min-w-0 overflow-hidden">
            {data.renderLeadQualityBadge(data.getLeadQualityValue(call), call)}
          </div>
          <div className="text-sm min-w-0 pr-2">
            <div className="max-h-28 overflow-y-auto custom-scrollbar">
              {data.renderEnrichmentFields(call)}
            </div>
          </div>
          <div className="text-sm text-gray-900 whitespace-nowrap min-w-0 overflow-hidden">
            {data.formatDuration(call.duration)}
          </div>
          <div className="text-sm text-gray-900 whitespace-nowrap min-w-0 overflow-hidden">
            {data.formatDate(call.createdAt || call.created_at)}
          </div>
          <div className="text-sm font-medium min-w-0">
            <div className="flex flex-col space-y-1">
              <div className="flex items-center space-x-2">
                {(() => {
                  const hasRecording = call.recording && call.recording.trim() !== '';
                  const effectiveRecordingStatus = hasRecording && (!call.recording_status || call.recording_status === 'none')
                    ? 'completed'
                    : (call.recording_status || 'none');
                  
                  return (
                    <>
                      <span className={`text-xs px-2 py-1 rounded ${
                        effectiveRecordingStatus === 'completed' ? 'bg-green-100 text-green-800' :
                        effectiveRecordingStatus === 'failed' ? 'bg-red-100 text-red-800' :
                        effectiveRecordingStatus === 'recording' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        🎵 {effectiveRecordingStatus}
                      </span>
                      {effectiveRecordingStatus === 'completed' && (
                        <button
                          onClick={() => data.downloadCallRecording(call._id)}
                          className="text-blue-600 hover:text-blue-900 text-xs"
                          title="Télécharger l'enregistrement"
                        >
                          📥
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
              <div className="flex items-center space-x-2">
                {(() => {
                  const hasTranscript = call.transcript && Array.isArray(call.transcript) && call.transcript.length > 0;
                  const effectiveTranscriptionStatus = hasTranscript && (!call.transcription_status || call.transcription_status === 'pending')
                    ? 'completed'
                    : (call.transcription_status || 'pending');
                  
                  return (
                    <>
                      <span className={`text-xs px-2 py-1 rounded ${
                        effectiveTranscriptionStatus === 'completed' ? 'bg-green-100 text-green-800' :
                        effectiveTranscriptionStatus === 'failed' ? 'bg-red-100 text-red-800' :
                        effectiveTranscriptionStatus === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        📄 {effectiveTranscriptionStatus}
                      </span>
                      {effectiveTranscriptionStatus === 'completed' && (
                        <button
                          onClick={() => data.downloadCallTranscript(call._id)}
                          className="text-green-600 hover:text-green-900 text-xs"
                          title="Télécharger la transcription"
                        >
                          📥
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  });

  TableRow.displayName = 'TableRow';

  if (calls.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 py-8">
        No calls found
      </div>
    );
  }

  return (
    <div className="relative bg-white border rounded-lg">
      {/* Table Header */}
      <div className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
        <div className="grid grid-cols-[minmax(100px,1fr)_minmax(100px,1fr)_minmax(80px,0.8fr)_minmax(100px,1fr)_minmax(100px,1fr)_minmax(200px,2fr)_minmax(60px,0.6fr)_minmax(120px,1.2fr)_minmax(150px,1.5fr)] gap-3 px-6 py-3">
          <div className="text-xs font-medium text-gray-500 uppercase min-w-0">Contact</div>
          <div className="text-xs font-medium text-gray-500 uppercase min-w-0">Téléphone</div>
          <div className="text-xs font-medium text-gray-500 uppercase min-w-0">Statut</div>
          <div className="text-xs font-medium text-gray-500 uppercase min-w-0">Catégorie</div>
          <div className="text-xs font-medium text-gray-500 uppercase min-w-0">Lead Quality</div>
          <div className="text-xs font-medium text-gray-500 uppercase min-w-0 max-w-md">Champs d'enrichissement</div>
          <div className="text-xs font-medium text-gray-500 uppercase min-w-0">Durée</div>
          <div className="text-xs font-medium text-gray-500 uppercase min-w-0">Date</div>
          <div className="text-xs font-medium text-gray-500 uppercase min-w-0">Enregistrement & Transcription</div>
        </div>
      </div>
      
      {/* Virtual Scrolling Body */}
      <FixedSizeList
        height={height}
        itemCount={calls.length}
        itemSize={120}
        width="100%"
        itemData={{
          calls,
          contacts,
          getStatusBadge,
          getCallCategoryValue,
          getLeadQualityValue,
          renderCategoryBadge,
          renderLeadQualityBadge,
          renderEnrichmentFields,
          formatDuration,
          formatDate,
          downloadCallRecording,
          downloadCallTranscript
        }}
        overscanCount={5}
      >
        {TableRow}
      </FixedSizeList>
      
      {(hasMore || loading) && (
        <div 
          ref={observerTarget} 
          className="h-10 flex items-center justify-center py-4 border-t border-gray-200"
        >
          {loading && <div className="text-gray-500">Loading more calls...</div>}
        </div>
      )}
    </div>
  );
};

export default memo(VirtualCallTable);

